import {
  Cesium3DTileset,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  Cesium3DTileFeature,
  Model,
  Cartesian2,
  Cartesian3,
  Matrix3,
  Matrix4,
  Transforms,
  Color,
  createGuid,
  EllipsoidTerrainProvider,
  Primitive,
  GeometryInstance,
  PolylineGeometry,
  PolylineMaterialAppearance,
  Material,
  CallbackProperty,
  BoundingSphere,
  Entity,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";
import EditMove from "../edit/EditMove";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";
import PointEntity from "../edit/PointEntity";
import LinestringEntity from "../edit/LinestringEntity";
import PolygonEntity from "../edit/PolygonEntity";

/**
 *
 * @private
 * @param {*} selectFeature
 * @returns {string}
 */
const getSelectFeatureId = (selectFeature) => {
  let id;
  if (typeof selectFeature?.id === "string") {
    id = selectFeature.id;
  } else if (selectFeature?.id instanceof Entity) {
    id = selectFeature.id.id;
  }

  return id;
};

/**
 * Interaction 클래스에서 지원하는 object 추출
 */
const getSelectObject = (viewer, selectFeature) => {
  let selectObject;
  if (defined(selectFeature)) {
    // DitapStorage 체크
    const id = getSelectFeatureId(selectFeature);
    const ditapStorageInstance = viewer.ditapStorage.getInstanceById(id);
    if (ditapStorageInstance) {
      if (
        ditapStorageInstance instanceof PointEntity ||
        ditapStorageInstance instanceof LinestringEntity ||
        ditapStorageInstance instanceof PolygonEntity
      ) {
        selectObject = ditapStorageInstance;
      } else {
        return;
      }
    }

    // DitapStorage 데이터가 아닌 경우
    // Scale에서 Entity는 지원하지 않음
    if (!selectObject) {
      if (selectFeature?.id instanceof Entity) {
        selectObject = selectFeature.id;
      } else if (selectFeature?.primitive?.id instanceof Entity) {
        selectObject = selectFeature.primitive.id;
      } else if (selectFeature instanceof Cesium3DTileFeature) {
        selectObject = selectFeature.tileset;
      } else if (selectFeature?.primitive instanceof Cesium3DTileset) {
        selectObject = selectFeature.primitive;
      } else if (selectFeature?.primitive instanceof Model) {
        selectObject = selectFeature.primitive;
      }
    }
  }

  return selectObject;
};

/**
 * 객체 이동을 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input (mouse/keyboard) class for object movement.
 * @alias InteractionMove
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {function} [callback=undefined] Interaction 종료 콜백.</br> Interaction end callback.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 실행
 * const interactionMove = new Ditap.InteractionMove(viewer);
 * interactionMove.on();
 */
class InteractionMove {
  constructor(viewer, callback) {
    this._viewer = undefined;
    this._handler = undefined;
    this._cameraRotate = undefined;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      this._handler = new ScreenSpaceEventHandler(viewer.canvas);
      this._cameraRotate =
        viewer.scene.screenSpaceCameraController.enableRotate;
    } else {
      throw Error("InteractionMove viewer type error");
    }

    // callback
    this._callback = undefined;
    if (callback instanceof Function) {
      this._callback = callback;
    } else if (callback === undefined) {
      this._callback = undefined;
    } else {
      throw Error("InteractionMove callback type error");
    }

    // Interaction 상태값
    this._isOn = false;
    this._isLeftDown = false;

    // Interaction 객체
    this._axis = undefined;
    this._selectAxis = undefined;
    this._selectAxisName = undefined;
    this._selectObject = undefined;

    // Interaction 좌표
    this._mousePosition = undefined;
    this._originalPosition = undefined;
    this._translation = Cartesian3.ZERO;
    this._position = undefined;

    // Interaction 이벤트
    this._keyDownEvent = undefined;
    this._mousemoveEvent = undefined;

    // Interaction 팝업
    this._infoLabelEntity = undefined;
    this._buttonElement = undefined;
    this._offAlert = undefined;
    this._instantAlert = undefined;
    this._isMobile = undefined;
    this.cancelBtn = undefined;
    this.endBtn = undefined;
    this.cancelEvent = undefined;
    this.endEvent = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /*
   * 축 엔티티 배열.<br>
   * Array of axis entities.
   * @type {Entity[]}
   */
  get axis() {
    return this._axis;
  }
  set axis(axis) {
    this._axis = axis;
  }

  /**
   * 핸들러 비/활성화 상태.</br>
   * Handler enabled or disabled.
   * @readonly
   * @type {boolean}
   */
  get isOn() {
    return this._isOn;
  }

  /*
   * 마우스 왼 클릭 여부.<br>
   * Mouse left down state.
   * @type {boolean}
   */
  get isLeftDown() {
    return this._isLeftDown;
  }
  set isLeftDown(isLeftDown) {
    this._isLeftDown = isLeftDown;
  }

  /*
   * 카메라 회전 여부.<br>
   * Camera rotation state.
   * @type {boolean}
   */
  get cameraRotate() {
    return this._cameraRotate;
  }
  set cameraRotate(cameraRotate) {
    this._cameraRotate = cameraRotate;
  }

  /*
   * 선택된 축 엔티티.<br>
   * Select axis entity.
   * @type {Entity}
   */
  get selectAxis() {
    return this._selectAxis;
  }
  set selectAxis(selectAxis) {
    this._selectAxis = selectAxis;
  }

  /*
   * 선택된 축 이름.<br>
   * Select axis name.
   * @type {Entity}
   */
  get selectAxisName() {
    return this._selectAxisName;
  }
  set selectAxisName(selectAxisName) {
    this._selectAxisName = selectAxisName;
  }

  /*
   * 선택된 객체.<br>
   * Select Object.
   * @type {Model | Cesium3DTileset}
   */
  get selectObject() {
    return this._selectObject;
  }
  set selectObject(selectObject) {
    this._selectObject = selectObject;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  get mousePosition() {
    return this._mousePosition;
  }
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 윈도우에서 마우스 이동 시 실행 이벤트.</br>
   * Events to run when mouse move in the window.
   * @type {Function}
   */
  get mousemoveEvent() {
    return this._mousemoveEvent;
  }
  set mousemoveEvent(mousemoveEvent) {
    this._mousemoveEvent = mousemoveEvent;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼 요소.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /**
   * 종료 매서드 콜백함수.<br>
   * Off method callback.
   * @type {Function | undefined}
   */
  get callback() {
    return this._callback;
  }
  set callback(callback) {
    if (callback instanceof Function || callback instanceof undefined) {
      this._callback = callback;
    }
  }

  /**
   * 적용된 이동값.<br>
   * Applied translation value.
   * @readonly
   * @type {Cartesian3}
   */
  get translation() {
    return this._translation;
  }

  /*
   * 종료 시 알림창.<br>
   * off method alert
   * @type {DitapAlert}
   */
  get offAlert() {
    return this._offAlert;
  }
  set offAlert(offAlert) {
    this._offAlert = offAlert;
  }

  /*
   * 모델의 원래 위치.<br>
   * The original position of the model.
   * @type {Cartesian3}
   */
  get originalPosition() {
    return this._originalPosition;
  }

  // computeIntersection(lineOrigin, lineDirection, normal) {
  //   // 법선 벡터와 직선 벡터의 내적을 구합니다.
  //   const ndotu = Cartesian3.dot(normal, lineDirection);

  //   // ndotu가 0이면 법선 벡터와 직선 벡터는 평행하므로 교점은 없습니다.
  //   if (Math.abs(ndotu) === 0) {
  //     return undefined;
  //   }

  //   // 교점을 구합니다.
  //   const w = Cartesian3.subtract(
  //     lineOrigin,
  //     Cartesian3.ZERO,
  //     new Cartesian3()
  //   );
  //   const t = -Cartesian3.dot(normal, w) / ndotu;
  //   const intersection = Cartesian3.add(
  //     lineOrigin,
  //     Cartesian3.multiplyByScalar(lineDirection, t, new Cartesian3()),
  //     new Cartesian3()
  //   );

  //   return intersection;
  // }

  //        p1
  //        |
  // p0    foot    p2
  // calcFootPoint(p0, p1, p2) {
  //   const t = this.calculateFootPointMoveValue(p0, p1, p2);

  //   const A = p0;
  //   const B = p2;
  //   const AB = Cartesian3.subtract(B, A, new Cartesian3());
  //   const ABtb = Cartesian3.multiplyByScalar(AB, t, new Cartesian3());

  //   const footPosition = Cartesian3.add(A, ABtb, new Cartesian3());

  //   return footPosition;
  // }

  // calculateFootPointMoveValue(p0, p1, p2) {
  //   const A = p0;
  //   const B = p2;
  //   const C = p1;

  //   const AB = Cartesian3.subtract(B, A, new Cartesian3());
  //   const AC = Cartesian3.subtract(C, A, new Cartesian3());

  //   const top = Cartesian3.dot(AB, AC);
  //   const bottom = Cartesian3.dot(AB, AB);

  //   const t = top / bottom;

  //   return t;
  // }

  /*
   * 모델 되돌리기.<br>
   * model rollback
   * @returns {Model | Cesium3DTileset | undefined} 적용된 모델.<br> Model in interaction
   */
  rollback() {
    const translation = this._translation;
    const selectObject = this.selectObject;

    if (translation instanceof Cartesian3 && selectObject) {
      this._translation = Cartesian3.ZERO;

      EditMove.setPosition(selectObject, this._originalPosition);

      this.updateAxis();
    }

    return selectObject;
  }

  /*
   * 마우스 위치를 기반으로 각도 계산.<br>
   * Calculate angle based on mouse position.
   * @private
   * @param {Cartesian3} position 기준점. Datum point.
   * @param {Cartesian2} startWindowPosition 마우스 시작 지점의 창 좌표.<br> Mouse starting point window coordinates
   * @param {Cartesian2} endWindowPosition 마우스 종료 지점의 창 좌표. Mouse endpoint window coordinates
   * @param {string} axis 선택 축(X, Y, Z).<br> Select axis ["X", "Y", "Z"]
   * @returns {Cartesian3} 이동 값.<br> Movement value
   */
  calculateMovementValue(
    position,
    startWindowPosition,
    endWindowPosition,
    axis
  ) {
    const viewer = this.viewer;
    //const decompositionMatrix = this.editMove.decompositionMatrix(object);
    //const position = decompositionMatrix.position;

    //# set vector
    let vector;
    switch (axis) {
      case "X":
        vector = this.getAxisVector(position, axis);
        break;
      case "Y":
        vector = this.getAxisVector(position, axis);
        break;
      case "Z":
        vector = this.getAxisVector(position, axis);
        break;
      default:
        return;
    }

    const windowMovement = Cartesian2.distance(
      startWindowPosition,
      endWindowPosition,
      new Cartesian2()
    );
    const scale = Cartesian3.distance(viewer.camera.position, position) / 1000;
    let distance = windowMovement * scale;

    //# mouse vector
    const mouseVector = Cartesian2.subtract(
      endWindowPosition,
      startWindowPosition,
      new Cartesian2()
    );

    //# axis cartesian -> window vector
    const windowVectorToCesiumPosition = Cartesian3.add(
      position,
      vector,
      new Cartesian3()
    );
    const firstWindow = viewer.scene.cartesianToCanvasCoordinates(position);
    const secondWindow = viewer.scene.cartesianToCanvasCoordinates(
      windowVectorToCesiumPosition
    );

    const axisWindowVector = Cartesian2.subtract(
      secondWindow,
      firstWindow,
      new Cartesian2()
    );

    //# mouse vector * axis window vector
    const final = Cartesian2.multiplyComponents(
      mouseVector,
      axisWindowVector,
      new Cartesian3()
    );

    //# set direction
    distance *= (final.x + final.y) / Math.abs(final.x + final.y);

    //# compute translation value
    const sum = Cartesian3.multiplyByScalar(vector, distance, new Cartesian3());

    if (isNaN(sum.x) || isNaN(sum.y) || isNaN(sum.z)) {
      return;
    }

    return sum;
  }

  /*
   * 축 엔티티 제거.<br>
   * Remove axis entities
   * @private
   */
  removeAxis() {
    const viewer = this.viewer;
    const axis = this.axis;

    for (const entity of axis) {
      //viewer.entities.remove(entity);
      viewer.scene.primitives.remove(entity);
    }

    this.axis = undefined;
    this.selectAxis = undefined;
    this.selectAxisName = undefined;
  }

  /*
   * 축 매트릭스 수정.<br>
   * Update axis matrix
   * @private
   */
  updateAxis() {
    const translation = this.translation;

    const transform = Matrix4.fromTranslation(translation, new Matrix4());

    // axis 행렬 업데이트
    const axisList = this.axis;
    for (const axis of axisList) {
      axis.modelMatrix = transform;
    }
  }

  /*
   * Ditap Viewer에 폴리라인 엔티티 생성.</br>
   * Create a polylineEntity in the DitapViewer.
   * @private
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {string} id 폴리라인 아이디.<br> Polyline id
   * @param {Cartesian3[]} positions 폴리라인 생성 좌표 배열.</br> Cartesians of polylineEntity.
   * @param {Color} [color=Color.RED] 생성할 폴리라인 색상.<br> Polyline color
   * @param {number} [width=3] 생성할 폴리라인 너비.<br> Polyline width
   * @returns {Entity} 생성된 폴리라인 엔티티. </br> Created polylineEntity.
   */
  createPolyline(viewer, id, positions, color = Color.RED, width = 3) {
    // const polyline = viewer.entities.add({
    //   id: id || undefined,
    //   name: name,
    //   polyline: {
    //     positions: positions,
    //     width: width,
    //     material: new PolylineArrowMaterialProperty(color),
    //     depthFailMaterial: Color.GRAY,
    //   },
    // });
    const polyline = viewer.scene.primitives.add(
      new Primitive({
        //# geomtry 설정
        geometryInstances: new GeometryInstance({
          id: id || createGuid(), //ID
          geometry: new PolylineGeometry({
            //position, width
            positions: positions,
            width: width || 5,
          }),
        }),
        //# 입력된 geomretryInstances 생상 설정
        appearance: new PolylineMaterialAppearance({
          material: new Material({
            fabric: {
              type: "PolylineArrow", //fabric 타입에 따라 polyline 모양이 바뀜
              uniforms: {
                color: color,
              },
            },
          }),
        }),
        //# 입력된 geometryInstance가 depthfail인 경우 Alpha 색상 적용
        depthFailAppearance: new PolylineMaterialAppearance({
          material: new Material({
            fabric: {
              type: "PolylineArrow",
              uniforms: {
                color: color.withAlpha(0.35),
                //color: color
              },
            },
          }),
        }),
        asynchronous: false, //비동기 해제
        //modelMatrix: matrix
      })
    );

    return polyline;
  }

  /*
   * 입력 좌표의 X,Y,Z 벡터를 계산.<br>
   * Compute eastnorthup XYZ vector of input coordinates
   * @private
   * @param {Cartesian3} position 계산할 기준점.<br> Calculation base point
   * @param {string | undefined} [axis=undefined] 축(X, Y, Z). 정의되지 않은 경우 모든 축 벡터 반환.</br> Axis is 'X', 'Y', 'Z'. When the undefined, return all axis vector
   * @return {Cartesian3 | Cartesian3[]} 계산된 축 벡터.<br> Axis vector
   */
  getAxisVector(position, axis = undefined) {
    // axis vector
    const modelMatrix = Transforms.eastNorthUpToFixedFrame(position); //# NorthEastUp 변환
    const matrix3 = Matrix4.getMatrix3(modelMatrix, new Matrix3()); //# XYZ 벡터 추출
    const vector = [
      Matrix3.getColumn(matrix3, 0, new Cartesian3()),
      Matrix3.getColumn(matrix3, 1, new Cartesian3()),
      Matrix3.getColumn(matrix3, 2, new Cartesian3()),
    ];

    let result;
    if (axis === "X") {
      result = vector[0];
    } else if (axis === "Y") {
      result = vector[1];
    } else if (axis === "Z") {
      result = vector[2];
    } else {
      result = vector;
    }

    return result;
  }

  /*
   * 축 엔티티 생성.<br>
   * Create axis entities
   * @private
   * @param {Cartesian3} position 축 생성 좌표.<br> Axis creation coordinates.
   * @param {number} radius 반지름.<br> Radius.
   * @returns {Entity[]} 생성된 축 엔티티 배열.</br> [X axis entity, Y axis entity, Z axis entity]
   */
  createAxis(position, radius) {
    // East-North-Up vector from center position
    const vector = this.getAxisVector(position);
    // axis length
    //const half = object.boundingSphere.radius / 2;
    const half = radius / 2;
    const length = half < 50 ? 50 : half;
    // axis width => 화살표는 좀 더 굵게 해줘야함
    const width = 36;

    // axis info
    //X_AXIS
    const x_axis = [
      `InteractionMove_X_AXIS_${createGuid()}`, // id
      [Color.RED, width], // color, width
      vector[0],
    ];
    //Y_AXIS
    const y_axis = [
      `InteractionMove_Y_AXIS_${createGuid()}`,
      [Color.GREEN, width],
      vector[1],
    ];
    //Z_AXIS
    const z_axis = [
      `InteractionMove_Z_AXIS_${createGuid()}`,
      [Color.BLUE, width],
      vector[2],
    ];

    // create axis
    const axis_list = [];
    for (const axis of [x_axis, y_axis, z_axis]) {
      const pos = [
        position,
        new Cartesian3.add(
          position,
          new Cartesian3.multiplyByScalar(axis[2], length, new Cartesian3()),
          new Cartesian3()
        ),
      ];

      const entity = this.createPolyline(
        this.viewer,
        axis[0], // ID
        pos, // positions
        axis[1][0], // Color
        axis[1][1] // Polyline Width
      );

      axis_list.push(entity);
    }

    return axis_list;
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   * @private
   */
  createRightClickHandler() {
    const handler = this._handler;

    handler.setInputAction((e) => {
      // axis 제거
      // if (this.selectObject) {
      //   if (this.axis) {
      //     this.removeAxis();
      //   }
      //   this.selectObject = undefined;
      // }

      if (this.selectObject) {
        this.off();
      } else {
        this.off(false);
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   * @private
   */
  createLeftClickHandler() {
    const viewer = this._viewer;
    const handler = this._handler;

    handler.setInputAction((e) => {
      const selectFeature = viewer.scene.pick(e.position);
      if (!this.selectObject && selectFeature) {
        const selectObject = getSelectObject(viewer, selectFeature);
        if (selectObject) {
          this.selectObject = selectObject;

          // Axis Radius, 기준점 설정
          let radius;
          if (this.selectObject instanceof Entity) {
            // 기준점 지정
            const position = Utils.getPropertyValue(this.selectObject.position);
            if (!position) {
              this._instantAlert.open();
            }
            this._originalPosition = position.clone();
            this._position = position;

            // BoundingSphere 추출
            const boundingSphere = new BoundingSphere();
            viewer.dataSourceDisplay.getBoundingSphere(
              this.selectObject,
              true,
              boundingSphere
            );
            radius = boundingSphere.radius;

            // Entity position CallbackProperty 설정
            EditMove.setPosition(
              this.selectObject,
              new CallbackProperty(() => {
                return this._position;
              }, false)
            );

            // DitapGeometry
          } else if (
            this.selectObject instanceof PointEntity ||
            this.selectObject instanceof LinestringEntity ||
            this.selectObject instanceof PolygonEntity
          ) {
            const boundingSphere = this.selectObject.computeBoundingSphere();
            if (!boundingSphere) {
              this._instantAlert.open();
            }
            this._originalPosition = boundingSphere.center.clone();
            this._position = boundingSphere.center;

            // Axis 크기
            radius = boundingSphere.radius;

            // Entity position CallbackProperty 설정
            EditMove.setPosition(
              this.selectObject,
              new CallbackProperty(() => {
                return this._position;
              }, false)
            );

            // Primitive
          } else {
            // BoundingSphere 추출 및 기준점 지정
            const boundingSphere = this.selectObject.boundingSphere;
            this._originalPosition = boundingSphere.center.clone();
            this._position = boundingSphere.center;

            // Axis 크기
            radius = boundingSphere.radius;
          }

          // 기준점 없으면 예외처리
          if (!defined(this._originalPosition) || !defined(this._position)) {
            this._instantAlert.open();
          }

          // Axis 생성
          this.axis = this.createAxis(this._originalPosition, radius);
          this.updateAxis();

          // 인포 라벨 제거
          if (!this._isMobile) {
            // 인포 라벨 제거
            this.infoLabelEntity.remove();
          }

          if (this.endBtn) {
            this.endBtn.style.display = "flex";
          }
          if (this.cancelBtn) {
            this.cancelBtn.style.display = "none";
          }
        } else {
          this._instantAlert.open();
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 왼쪽 버튼 누를 경우 실행 이벤트 생성.</br>
   * Mouse left down cesium handler.
   * @private
   */
  createLeftDownHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const pickedFeature = viewer.scene.pick(e.position);

      let selectAxis;
      let selectAxisName;
      if (this.selectObject && pickedFeature?.primitive instanceof Primitive) {
        if (this.axis.includes(pickedFeature.primitive)) {
          this.isLeftDown = true;
          viewer.scene.screenSpaceCameraController.enableRotate = false;

          selectAxis = pickedFeature.primitive;
          selectAxisName = pickedFeature.id.split("_")[1];
        }
      }

      this.selectAxis = selectAxis;
      this.selectAxisName = selectAxisName;
    }, ScreenSpaceEventType.LEFT_DOWN);
  }

  /*
   * 마우스 왼쪽 버튼을 뗄 경우 실행 이벤트 생성.</br>
   * Mouse left up cesium handler.
   * @private
   */
  createLeftUpHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.isLeftDown = false;
      this.selectAxis = undefined;
      this.selectAxisName = undefined;
      viewer.scene.screenSpaceCameraController.enableRotate = this._cameraRotate;
    }, ScreenSpaceEventType.LEFT_UP);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   * @private
   */
  createMouseMoveHandler() {
    const viewer = this.viewer;
    const handler = this.handler;
    handler.setInputAction((e) => {
      const isLeftDown = this.isLeftDown;
      const selectAxis = this.selectAxis;
      const selectObject = this.selectObject;
      const selectAxisName = this.selectAxisName;

      if (isLeftDown) {
        if (selectAxis) {
          const movementValue = this.calculateMovementValue(
            this._position,
            e.startPosition,
            e.endPosition,
            selectAxisName
          );

          if (movementValue) {
            // 원점으로부터 총 이동량 계산
            const totalTranslation = Cartesian3.add(
              this._translation,
              movementValue,
              new Cartesian3()
            );
            this._translation = totalTranslation;

            // 원점 + 총 이동량
            const newPosition = Cartesian3.add(
              this._originalPosition,
              totalTranslation,
              new Cartesian3()
            );
            this._position = newPosition;

            // 모델 위치 업데이트 => Entity는 this._position을 CallbackProperty로 설정해놓음.
            if (
              !(
                this.selectObject instanceof Entity ||
                this.selectObject instanceof PointEntity ||
                this.selectObject instanceof LinestringEntity ||
                this.selectObject instanceof PolygonEntity
              )
            ) {
              EditMove.addPosition(selectObject, movementValue);
            }

            // AXIS 업데이트
            this.updateAxis();
          }
        } else {
          console.error("InteractionMove Move selectAxis undefined");
        }
      }

      const isMobile = Utils.detectDeviceType();
      if (!isMobile && !this.selectObject) {
        // 마우스 좌표 설정
        const position = this.pickPosition(e.endPosition, viewer);
        if (defined(position)) {
          this.mousePosition = position;

          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(
            viewer,
            this.mousePosition,
            this.infoLabelEntity
          );
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /**
   * 객체 이동 핸들러 실행. <br>
   * Execute object movement handler.
   */
  on() {
    if (!this._isOn) {
      this._isMobile = Utils.detectDeviceType();
      this.viewer.currentEvent = this;
      this._isOn = true;
      this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      this.offAlert = new DitapAlert({
        type: "question",
        title: "편집 종료",
        content1: "확인 : 편집 적용",
        content2: "취소 : 편집 초기화",
        buttons: "OKANDCANCEL",
        open: false,
        callback: () => {
          if (this._callback instanceof Function) {
            try {
              this.callback(this._translation);
            } catch (e) {
              this.rollback();
            }
          }
          this._offAlert.close();
          this._selectObject = undefined;
          this._translation = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._position = undefined;
        },
        closeCallback: () => {
          this.rollback();
          this._offAlert.close();
          this._selectObject = undefined;
          this._translation = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._position = undefined;
        },
      });

      this._instantAlert = new DitapAlert({
        type: "info",
        title: "이동 편집",
        content1: "해당 객체는 이동 편집을",
        content2: "지원하지 않습니다.",
        buttons: "ONLYOK",
        open: false,
      });

      // 경고창 - X 버튼 이벤트 => 콜백 바인딩 해제
      const oldElement = this._offAlert.closeBtn;
      const newElement = oldElement.cloneNode(true);
      newElement.addEventListener("click", () => {
        this._offAlert.close();
      });
      this._offAlert.closeBtn.replaceWith(newElement);

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여",
        "이동 모델을 선택해주세요",
        true
      );

      this.infoLabel = Utils.createInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 이동 모델을 선택해주세요"
      );

      if (this._isMobile) {
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
        this.cancelBtn.style.display = this.selectObject ? "none" : "flex";
        this.endBtn.style.display = this.selectObject ? "flex" : "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createLeftClickHandler();
      this.createLeftDownHandler();
      this.createLeftUpHandler();
      this.createResizeEvent();
      this.createMouseMoveHandler();

      //this.createKeyDownHandler();
    }
  }

  /**
   * 객체 이동 핸들러 종료. (EditMove 초기화 필수.) <br>
   * End object movement handler. (EditMove initialization is required.)
   * @param {boolean} [alert=true] 종료 팝업 오픈 여부.<br> alert If true, pops up the exit popup.
   * @param {function} [callback=undefined] 팝업을 통해 실행 될 콜백 함수.<br> function at the end of interaction via popup
   */
  off(alert = true, callback = undefined) {
    if (this._isOn) {
      const offInteraction = () => {
        // Entity에 설정한 CallbackProperty 해제
        if (
          this.selectObject instanceof Entity ||
          this.selectObject instanceof PointEntity ||
          this.selectObject instanceof LinestringEntity ||
          this.selectObject instanceof PolygonEntity
        ) {
          EditMove.setPosition(this.selectObject, this._position);
        }

        // axis 제거
        if (this.axis) {
          this.removeAxis();
        } else {
          this.axis = undefined;
          this.selectAxis = undefined;
          this.selectAxisName = undefined;
        }

        // 도움말 라벨 제거.
        this.mobileInfoLabel?.label.remove();
        this.infoLabel?.remove();

        // 버튼 비활성화
        this.buttonElement?.classList.remove("on");

        if (!this.handler?.isDestroyed()) {
          this.handler?.destroy();
        }

        window.removeEventListener("keydown", this.keyDownEvent);
        window.removeEventListener("mousemove", this.mousemoveEvent);
        // this.removeChangeEvent();

        if (this._isMobile) {
          this.removeMobileEvent();
        }

        if (this.resizeEvent) {
          window.removeEventListener("resize", this.resizeEvent);
        }

        this.handler = undefined;
        this._isOn = false;
        this.selectAxis = undefined;
        this.selectObject = undefined;
        this.mousePosition = undefined;
        this._originalPosition = undefined;
        this._translation = Cartesian3.ZERO;
        this._position = undefined;
        this.cancelBtn = undefined;
        this.endBtn = undefined;
        this.infoLabelEntity = undefined;
        this.mobileInfoLabel = undefined;
        this.infoLabel = undefined;

        this._isMobile = undefined;
        // 변수 초기화 = 인포 라벨
        this.mousemoveEvent = undefined;
        this.viewer.currentEvent = undefined;
      };

      // 알림창 없이 종료(선택된 객체가 없을 때)
      if (!this.selectObject) {
        // Interaction 종료
        offInteraction();

        // 알림창 open
      } else if (alert && this.selectObject) {
        // Right Click ContextMenu 제거
        const contextMenu = document.oncontextmenu;
        document.oncontextmenu = (e) => {
          e.preventDefault();
        };

        this.offAlert.open();
        this.offAlert.callback = (alert) => {
          // Interaction 종료
          offInteraction();
          // Right Click ContextMenu 복구
          document.oncontextmenu = contextMenu;
          if (callback) {
            callback();
          }
          alert.destroy();
        };
        this.offAlert.closeCallback = (alert) => {
          this.rollback();
          this._selectObject = undefined;
          this._translation = Cartesian3.ZERO;
          this._originalPosition = undefined;
          this._position = undefined;
          offInteraction();
          if (callback) {
            callback();
          }
          alert.destroy();
        };
      }
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object
   */
  destroy() {
    if (this.axis) {
      this.removeAxis();
    }
    this.handler?.destroy();
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   * @private
   */
  createMobileEvent() {
    this?.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        if (this.selectObject) {
          this.off();
        } else {
          this.off(false);
        }
      })
    );

    this?.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        if (this.selectObject) {
          this.off();
        } else {
          this.off(false);
        }
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   * @private
   */
  removeMobileEvent() {
    if (this.endEvent) {
      this?.endBtn.removeEventListener("click", this.endEvent);
    }
    this.endEvent = undefined;
    this.endBtn = undefined;

    if (this.cancelEvent) {
      this?.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @private
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";
            this.cancelBtn.style.display = this.selectObject ? "none" : "flex";
            this.endBtn.style.display = this.selectObject ? "flex" : "none";

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /*
   * Terrain provider 여부에 따라 새로운 Cartesian 반환.</br>
   * Returns new cartesian based on ellipsoid terrain provider presence.
   * @private
   * @param {Cartesian3} position 재구성할 Cartesian 좌표.</br> The cartesian to be reconstructed.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Cartesian3} 재구성된 Cartesian 좌표. </br> Reconstructed based on ellipsoid terrain provider presence.
   */
  pickPosition(position, viewer) {
    let newPosition;
    let isEllipsoid = false;
    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      newPosition = viewer.scene.pickPosition(position);
    } else {
      newPosition = viewer.camera.pickEllipsoid(
        position,
        viewer.scene.globe.ellipsoid
      );
    }

    return newPosition;
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @private
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    // ESC : 체적 측정 실행 취소
    if (event.key === "Escape") {
      //this.off();
      //this.destroyInstance();
      if (this.selectObject) {
        this.off();
      } else {
        this.off(false);
      }
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   * @private
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}

export default InteractionMove;
